import { useEffect, useState } from "react";
import { api, API, errMsg, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, RefreshCw, Loader2, Download } from "lucide-react";

const actionStyle = (a) =>
  ({ excluded: "border-red-200 bg-red-50 text-red-700",
     restored: "border-green-200 bg-ok text-ok-foreground",
     marked_duplicate: "border-warn-border bg-warn text-warn-foreground",
     kept: "border-blue-200 bg-secondary text-secondary-foreground" }[a] ||
   "bg-muted text-muted-foreground");

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : `${fmtDate(iso)} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};

export const AuditTrail = ({ jobId, refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const exportAudit = (fmt) => {
    window.open(`${API}/audit/export?fmt=${fmt}&job_id=${jobId}`, "_blank");
  };

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/analysis/${jobId}/audit`);
      setEntries(data.entries || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [jobId, refreshKey]);

  return (
    <div className="rounded-lg border border-border bg-white" data-testid="audit-trail">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold">
          <History className="h-4 w-4 text-primary" /> Audit Trail
          <span className="text-xs font-normal text-muted-foreground">
            ({entries.length} immutable record{entries.length === 1 ? "" : "s"})
          </span>
        </h3>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="bg-white" onClick={() => exportAudit("csv")}
                  data-testid="audit-trail-export-csv-btn">
            <Download className="mr-1 h-3.5 w-3.5" /> CSV
          </Button>
          <Button size="sm" variant="outline" className="bg-white" onClick={() => exportAudit("xlsx")}
                  data-testid="audit-trail-export-xlsx-btn">
            <Download className="mr-1 h-3.5 w-3.5" /> XLSX
          </Button>
          <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="audit-refresh-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <div className="p-5">
        {error && <p className="text-sm text-red-700" data-testid="audit-error">{error}</p>}
        {!error && entries.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="audit-empty">
            No exclusion or duplicate-resolution actions recorded yet.
          </p>
        )}
        {entries.length > 0 && (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Date &amp; Time</th>
                  <th className="text-left">Transaction</th>
                  <th className="text-left">Action</th>
                  <th className="text-left">Field</th>
                  <th className="text-left">Old → New</th>
                  <th className="text-left">User</th>
                  <th className="text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-b border-border/60" data-testid={`audit-row-${i}`}>
                    <td className="whitespace-nowrap py-2 text-xs">{when(e.created_at)}</td>
                    <td className="max-w-[220px] truncate">
                      {e.description || "—"}
                      <span className="block text-[10px] text-muted-foreground">{e.transaction_id}</span>
                    </td>
                    <td><Badge variant="outline" className={actionStyle(e.action)}>{e.action_type || e.action}</Badge></td>
                    <td className="text-xs">{e.field || "status"}</td>
                    <td className="max-w-[220px] truncate text-xs text-muted-foreground">
                      {(e.previous_value ?? e.previous_status ?? "—")} → {(e.new_value ?? e.new_status ?? "—")}
                    </td>
                    <td className="text-xs">{e.user_email}</td>
                    <td className="max-w-[200px] truncate text-xs text-muted-foreground">{e.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Audit records are append-only — they cannot be edited or deleted, and are visible only to the
          document owner and administrators.
        </p>
      </div>
    </div>
  );
};
